import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { adminApi, promptsApi, modelsApi } from '../services/api';

// Dashboard Tab
const AdminDashboard: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['adminDashboard'],
    queryFn: () => adminApi.getDashboard().then((r) => r.data),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{data?.users?.total || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Active Users</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{data?.users?.active || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Analyses</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{data?.analyses?.total || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Today's Analyses</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{data?.analyses?.today || 0}</div>
        </div>
      </div>

      {/* LLM Stats */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">LLM Providers</h3>
        <div className="space-y-3">
          {data?.llm?.providers?.map((provider: any) => (
            <div key={provider.provider} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{provider.model}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{provider.provider}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-900 dark:text-white">{provider.requests} requests</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(provider.success_rate * 100).toFixed(1)}% success
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tier breakdown */}
      {data?.users?.by_tier && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Users by Tier</h3>
          <div className="space-y-2">
            {Object.entries(data.users.by_tier).map(([tier, count]: [string, any]) => (
              <div key={tier} className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">{tier}</span>
                <span className="font-medium text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Users Tab
const AdminUsers: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>({});

  const { data, isLoading } = useQuery({
    queryKey: ['adminUsers', search, page],
    queryFn: () => adminApi.getUsers({ skip: page * 20, limit: 20, search: search || undefined }).then((r) => r.data),
  });

  // Fetch tiers for dropdown
  const { data: tiersData } = useQuery({
    queryKey: ['adminTiers'],
    queryFn: () => import('../services/api').then(({ tierApi }) => tierApi.getAll().then(r => r.data)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      adminApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setEditingId(null);
      setEditData({});
    },
  });

  const resetUsageMutation = useMutation({
    mutationFn: (id: number) => adminApi.resetUserUsage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });

  const startEdit = (user: any) => {
    setEditingId(user.id);
    setEditData({
      email: user.email,
      full_name: user.full_name || '',
      tier_id: user.tier_id,
      is_verified: user.is_verified,
      is_admin: user.is_admin,
      is_active: user.is_active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: editData });
    }
  };

  const tiersList = tiersData?.items || [];

  return (
    <div className="space-y-4">
      <input
        type="text"
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="input max-w-md"
      />

      {isLoading ? (
        <div className="animate-pulse">Loading...</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Tier</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data?.items?.map((user: any) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  {/* Email */}
                  <td className="px-4 py-3">
                    {editingId === user.id ? (
                      <input
                        type="email"
                        value={editData.email || ''}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="input w-48 text-sm py-1"
                      />
                    ) : (
                      <span className="text-sm text-gray-900 dark:text-white">{user.email}</span>
                    )}
                  </td>
                  {/* Name */}
                  <td className="px-4 py-3">
                    {editingId === user.id ? (
                      <input
                        type="text"
                        value={editData.full_name || ''}
                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                        className="input w-32 text-sm py-1"
                        placeholder="Name"
                      />
                    ) : (
                      <span className="text-sm text-gray-600 dark:text-gray-300">{user.full_name || '-'}</span>
                    )}
                  </td>
                  {/* Tier */}
                  <td className="px-4 py-3">
                    {editingId === user.id ? (
                      <select
                        value={editData.tier_id || ''}
                        onChange={(e) => setEditData({ ...editData, tier_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="input w-28 text-sm py-1"
                      >
                        <option value="">No Tier</option>
                        {tiersList.map((tier: any) => (
                          <option key={tier.id} value={tier.id}>{tier.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-600 dark:text-gray-300">{user.tier_name || '-'}</span>
                    )}
                  </td>
                  {/* Status */}
                  <td className="px-4 py-3">
                    {editingId === user.id ? (
                      <div className="flex flex-col gap-1">
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={editData.is_verified || false}
                            onChange={(e) => setEditData({ ...editData, is_verified: e.target.checked })}
                            className="mr-1"
                          />
                          Verified
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={editData.is_admin || false}
                            onChange={(e) => setEditData({ ...editData, is_admin: e.target.checked })}
                            className="mr-1"
                          />
                          Admin
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={editData.is_active || false}
                            onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                            className="mr-1"
                          />
                          Active
                        </label>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.is_admin && (
                          <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            Admin
                          </span>
                        )}
                        {user.is_verified ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Verified
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                            Unverified
                          </span>
                        )}
                        {!user.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            Inactive
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Created */}
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-3">
                    {editingId === user.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEdit}
                          disabled={updateMutation.isPending}
                          className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          {updateMutation.isPending ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(user)}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Reset monthly usage counter for this user?')) {
                              resetUsageMutation.mutate(user.id);
                            }
                          }}
                          disabled={resetUsageMutation.isPending}
                          className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Reset monthly usage"
                        >
                          Reset
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {data?.total > 20 && (
            <div className="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {page * 20 + 1}-{Math.min((page + 1) * 20, data.total)} of {data.total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn btn-secondary btn-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * 20 >= data.total}
                  className="btn btn-secondary btn-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Prompts Tab
const AdminPrompts: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [changeDescription, setChangeDescription] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['adminPrompts'],
    queryFn: () => promptsApi.getAll({ include_inactive: true }).then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, content, changeDesc }: { id: number; content: string; changeDesc: string }) =>
      promptsApi.update(id, { content, change_description: changeDesc }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPrompts'] });
      setEditingId(null);
      setEditContent('');
      setChangeDescription('');
    },
  });

  const handleEdit = (prompt: any) => {
    setEditingId(prompt.id);
    setEditContent(prompt.content);
    setChangeDescription('');
  };

  const handleSave = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, content: editContent, changeDesc: changeDescription });
    }
  };

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6">
      {data?.items?.map((prompt: any) => (
        <div key={prompt.id} className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{prompt.name}</h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {prompt.prompt_type}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded ${prompt.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {prompt.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {editingId !== prompt.id && (
              <button onClick={() => handleEdit(prompt)} className="btn btn-secondary btn-sm">
                Edit
              </button>
            )}
          </div>

          {prompt.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{prompt.description}</p>
          )}

          {editingId === prompt.id ? (
            <div className="space-y-4">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="input w-full h-64 font-mono text-sm"
              />
              <input
                type="text"
                placeholder="Describe what changed..."
                value={changeDescription}
                onChange={(e) => setChangeDescription(e.target.value)}
                className="input w-full"
              />
              <div className="flex space-x-2">
                <button onClick={handleSave} disabled={updateMutation.isPending} className="btn btn-primary">
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditingId(null)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <pre className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
              {prompt.content}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
};

// Models Tab
const AdminModels: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editInputPrice, setEditInputPrice] = useState('');
  const [editOutputPrice, setEditOutputPrice] = useState('');

  const { data: configs, isLoading } = useQuery({
    queryKey: ['modelConfigs'],
    queryFn: () => modelsApi.getConfigs().then((r) => r.data),
  });

  const syncMutation = useMutation({
    mutationFn: () => modelsApi.syncModels(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelConfigs'] });
    },
  });

  const updatePricingMutation = useMutation({
    mutationFn: () => modelsApi.updateAllPricing(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['modelConfigs'] });
      alert(`Updated ${response.data.updated} models, skipped ${response.data.skipped}`);
    },
  });

  const removeWithoutPricingMutation = useMutation({
    mutationFn: () => adminApi.removeModelsWithoutPricing(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['modelConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['modelSelection'] });
      alert(`Removed ${response.data.removed_count} models without pricing`);
    },
  });

  const updateSinglePricingMutation = useMutation({
    mutationFn: ({ configId, inputPrice, outputPrice }: { configId: number; inputPrice?: string; outputPrice?: string }) =>
      modelsApi.updatePricing(configId, inputPrice, outputPrice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelConfigs'] });
      setEditingId(null);
    },
  });

  const startEdit = (config: any) => {
    setEditingId(config.id);
    setEditInputPrice(config.cost_per_1k_input_tokens || '');
    setEditOutputPrice(config.cost_per_1k_output_tokens || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditInputPrice('');
    setEditOutputPrice('');
  };

  const saveEdit = (configId: number) => {
    updateSinglePricingMutation.mutate({
      configId,
      inputPrice: editInputPrice || undefined,
      outputPrice: editOutputPrice || undefined,
    });
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'degraded': return 'bg-red-500';
      case 'inactive': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Click on a row to edit prices manually. Prices are per 1K tokens.
        </p>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              if (window.confirm('Remove all models without pricing from the database?')) {
                removeWithoutPricingMutation.mutate();
              }
            }}
            disabled={removeWithoutPricingMutation.isPending}
            className="btn btn-danger"
          >
            {removeWithoutPricingMutation.isPending ? 'Removing...' : 'Remove Without Pricing'}
          </button>
          <button onClick={() => updatePricingMutation.mutate()} disabled={updatePricingMutation.isPending} className="btn btn-secondary">
            {updatePricingMutation.isPending ? 'Updating...' : 'Update All Pricing'}
          </button>
          <button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} className="btn btn-primary">
            {syncMutation.isPending ? 'Syncing...' : 'Sync Models from Providers'}
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Provider</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Model</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Input $/1K</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Output $/1K</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Requests</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Health</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {configs?.map((config: any) => (
              <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">{config.provider}</td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono text-xs">{config.model_name}</td>
                <td className="px-4 py-3">
                  {editingId === config.id ? (
                    <input
                      type="text"
                      value={editInputPrice}
                      onChange={(e) => setEditInputPrice(e.target.value)}
                      className="input w-24 text-sm py-1"
                      placeholder="0.001"
                    />
                  ) : (
                    <span className={`text-sm ${config.cost_per_1k_input_tokens ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                      {config.cost_per_1k_input_tokens ? `$${config.cost_per_1k_input_tokens}` : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === config.id ? (
                    <input
                      type="text"
                      value={editOutputPrice}
                      onChange={(e) => setEditOutputPrice(e.target.value)}
                      className="input w-24 text-sm py-1"
                      placeholder="0.002"
                    />
                  ) : (
                    <span className={`text-sm ${config.cost_per_1k_output_tokens ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                      {config.cost_per_1k_output_tokens ? `$${config.cost_per_1k_output_tokens}` : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{config.total_requests}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${getHealthColor(config.health_status)}`} />
                    <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">{config.health_status}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {editingId === config.id ? (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => saveEdit(config.id)}
                        disabled={updateSinglePricingMutation.isPending}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-gray-500 hover:text-gray-700 text-sm font-medium ml-2"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEdit(config)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Costs Tab
const AdminCosts: React.FC = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ['costDashboard', days],
    queryFn: () => adminApi.getCostDashboard(days).then((r) => r.data),
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-end">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input w-auto">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Cost</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            ${parseFloat(data?.summary?.total_cost || '0').toFixed(4)}
          </div>
          <div className={`text-sm ${data?.summary?.cost_change_pct >= 0 ? 'text-red-500' : 'text-green-500'}`}>
            {data?.summary?.cost_change_pct >= 0 ? '+' : ''}{data?.summary?.cost_change_pct?.toFixed(1)}% vs prev period
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Analyses</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{data?.summary?.total_analyses || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Tokens</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {(data?.summary?.total_tokens || 0).toLocaleString()}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Avg Cost/Analysis</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            ${data?.summary?.total_analyses > 0 ? (parseFloat(data?.summary?.total_cost || '0') / data?.summary?.total_analyses).toFixed(6) : '0'}
          </div>
        </div>
      </div>

      {/* Costs by Model */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Costs by Model</h3>
        <div className="space-y-3">
          {data?.costs_by_model?.map((model: any) => (
            <div key={`${model.provider}-${model.model}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{model.model}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{model.provider} - {model.analyses_count} analyses</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 dark:text-white">${parseFloat(model.total_cost).toFixed(4)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{model.total_tokens?.toLocaleString()} tokens</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Users by Cost */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Users by Spending</h3>
        <div className="space-y-3">
          {data?.top_users?.slice(0, 10).map((user: any) => (
            <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{user.email}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user.analyses_count} analyses</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 dark:text-white">${parseFloat(user.total_cost).toFixed(4)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user.total_tokens?.toLocaleString()} tokens</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Errors Tab
const AdminErrors: React.FC = () => {
  const [errorType, setErrorType] = useState<string>('');
  const [page, setPage] = useState(0);

  const { data: breakdown } = useQuery({
    queryKey: ['errorBreakdown'],
    queryFn: () => adminApi.getErrorBreakdown().then((r) => r.data),
  });

  const { data: logs, isLoading } = useQuery({
    queryKey: ['errorLogs', errorType, page],
    queryFn: () => adminApi.getErrorLogs({
      error_type: errorType || undefined,
      skip: page * 20,
      limit: 20
    }).then((r) => r.data),
  });

  return (
    <div className="space-y-6">
      {/* Error Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Errors</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{breakdown?.total_errors || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Error Types</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {Object.keys(breakdown?.by_type || {}).length}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Affected Providers</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {Object.keys(breakdown?.by_provider || {}).length}
          </div>
        </div>
      </div>

      {/* Error Type Breakdown */}
      {breakdown?.by_type && Object.keys(breakdown.by_type).length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Errors by Type</h3>
          <div className="space-y-2">
            {Object.entries(breakdown.by_type).map(([type, count]: [string, any]) => (
              <div key={type} className="flex items-center justify-between">
                <button
                  onClick={() => setErrorType(type === errorType ? '' : type)}
                  className={`text-sm ${errorType === type ? 'text-primary-600 font-medium' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  {type}
                </button>
                <span className="font-medium text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Logs Table */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Message</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Provider</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-3 text-center">Loading...</td></tr>
            ) : logs?.items?.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-3 text-center text-gray-500">No errors found</td></tr>
            ) : logs?.items?.map((error: any) => (
              <tr key={error.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    {error.error_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-md truncate">
                  {error.error_message}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {error.provider || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {new Date(error.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// System Health Tab
const AdminSystem: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: () => adminApi.getSystemHealth().then((r) => r.data),
    refetchInterval: 10000,
  });

  const { data: llmHealth } = useQuery({
    queryKey: ['llmHealth'],
    queryFn: () => adminApi.getLLMHealth().then((r) => r.data),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Database Status */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Database</h3>
        <div className="flex items-center space-x-2">
          <span className={`w-3 h-3 rounded-full ${data?.database?.healthy ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-gray-900 dark:text-white">
            {data?.database?.healthy ? 'Healthy' : 'Unhealthy'}
          </span>
        </div>
      </div>

      {/* LLM Providers Health */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">LLM Providers</h3>
        <div className="space-y-3">
          {llmHealth?.map((provider: any) => (
            <div key={`${provider.provider}-${provider.model}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={`w-3 h-3 rounded-full ${provider.is_healthy ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{provider.model}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{provider.provider}</p>
                </div>
              </div>
              {provider.response_time_ms && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {provider.response_time_ms}ms
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Settings Tab - Model Selection & Backup Configuration
const AdminSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [defaultModel, setDefaultModel] = useState<number | null>(null);
  const [backup1, setBackup1] = useState<number | null>(null);
  const [backup2, setBackup2] = useState<number | null>(null);
  const [backup3, setBackup3] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['modelSelection'],
    queryFn: () => adminApi.getModelSelection().then((r) => r.data),
  });

  const { data: backupStats } = useQuery({
    queryKey: ['backupCallStats'],
    queryFn: () => adminApi.getBackupCallStats(30).then((r) => r.data),
  });

  // Initialize form when data loads
  React.useEffect(() => {
    if (data) {
      setDefaultModel(data.default_model_id);
      setBackup1(data.backup_model_1_id);
      setBackup2(data.backup_model_2_id);
      setBackup3(data.backup_model_3_id);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => adminApi.updateModelSelection({
      default_model_id: defaultModel,
      backup_model_1_id: backup1,
      backup_model_2_id: backup2,
      backup_model_3_id: backup3,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelSelection'] });
      setHasChanges(false);
    },
  });

  const handleChange = (setter: React.Dispatch<React.SetStateAction<number | null>>, value: string) => {
    setter(value ? parseInt(value, 10) : null);
    setHasChanges(true);
  };

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  const availableModels = data?.available_models || [];

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      'api_error': 'API Error',
      'timeout': 'Timeout',
      'rate_limit': 'Rate Limit',
      'ai_prefix_detected': 'AI Prefix Detected',
      'sentence_structure_changed': 'Invalid Response',
      'invalid_response': 'Invalid Response',
    };
    return labels[reason] || reason;
  };

  return (
    <div className="space-y-6">
      {/* Model Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Correction Model Selection
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Select the default model for text correction and up to 3 backup models that will be used if the primary model fails.
        </p>

        <div className="space-y-4">
          {/* Default Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Model
            </label>
            <select
              value={defaultModel || ''}
              onChange={(e) => handleChange(setDefaultModel, e.target.value)}
              className="input w-full"
            >
              <option value="">Select a model...</option>
              {availableModels.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.provider} / {m.model_name} {m.cost_per_1k_input ? `($${m.cost_per_1k_input}/1k in)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Backup Models */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Backup 1 (First fallback)
              </label>
              <select
                value={backup1 || ''}
                onChange={(e) => handleChange(setBackup1, e.target.value)}
                className="input w-full"
              >
                <option value="">None</option>
                {availableModels.filter((m: any) => m.id !== defaultModel).map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.provider} / {m.model_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Backup 2 (Second fallback)
              </label>
              <select
                value={backup2 || ''}
                onChange={(e) => handleChange(setBackup2, e.target.value)}
                className="input w-full"
              >
                <option value="">None</option>
                {availableModels.filter((m: any) => m.id !== defaultModel && m.id !== backup1).map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.provider} / {m.model_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Backup 3 (Last resort)
              </label>
              <select
                value={backup3 || ''}
                onChange={(e) => handleChange(setBackup3, e.target.value)}
                className="input w-full"
              >
                <option value="">None</option>
                {availableModels.filter((m: any) => m.id !== defaultModel && m.id !== backup1 && m.id !== backup2).map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.provider} / {m.model_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!hasChanges || saveMutation.isPending}
              className="btn btn-primary"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Model Selection'}
            </button>
          </div>
        </div>
      </div>

      {/* Backup Call Statistics */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Backup Call Statistics (Last 30 days)
        </h3>

        {backupStats?.total_backup_calls === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No backup calls recorded in the last 30 days.</p>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Backup Calls</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {backupStats?.total_backup_calls || 0}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">Trigger Reasons</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.keys(backupStats?.by_trigger_reason || {}).length}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">Models Affected</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.keys(backupStats?.by_original_model || {}).length}
                </div>
              </div>
            </div>

            {/* By Reason */}
            {backupStats?.by_trigger_reason && Object.keys(backupStats.by_trigger_reason).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">By Trigger Reason</h4>
                <div className="space-y-2">
                  {Object.entries(backupStats.by_trigger_reason).map(([reason, count]: [string, any]) => (
                    <div key={reason} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <span className={`text-sm ${reason.includes('prefix') || reason.includes('structure') ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {getReasonLabel(reason)}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Logs */}
            {backupStats?.recent_logs?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Backup Calls</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400">Time</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400">Reason</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400">Original Model</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400">Backup Level</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400">Success</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {backupStats.recent_logs.slice(0, 10).map((log: any) => (
                        <tr key={log.id}>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 text-xs rounded ${log.trigger_reason.includes('prefix') || log.trigger_reason.includes('structure') ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                              {getReasonLabel(log.trigger_reason)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            {backupStats.model_names?.[log.original_model_id] || `ID: ${log.original_model_id}`}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                            Level {log.backup_level}
                          </td>
                          <td className="px-3 py-2">
                            {log.backup_succeeded ? (
                              <span className="text-green-600 dark:text-green-400">Yes</span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Tiers Tab
const AdminTiers: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>({});

  // Fetch tiers using the tier API
  const { data: tiersData, isLoading } = useQuery({
    queryKey: ['adminTiers'],
    queryFn: () => import('../services/api').then(({ tierApi }) => tierApi.getAll().then(r => r.data)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      adminApi.updateTier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminTiers'] });
      setEditingId(null);
      setEditData({});
    },
  });

  const startEdit = (tier: any) => {
    setEditingId(tier.id);
    setEditData({
      name: tier.name,
      description: tier.description,
      text_limit: tier.text_limit,
      monthly_limit: tier.monthly_limit,
      weekly_cost_limit: tier.weekly_cost_limit,
      monthly_price: tier.monthly_price,
      yearly_price: tier.yearly_price,
      is_default: tier.is_default,
      priority: tier.priority,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: editData });
    }
  };

  const tiersList = tiersData?.items || [];

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage subscription tiers and pricing
        </p>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Text Limit</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Weekly Cost Limit</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Price</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Yearly Price</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Users</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {tiersList.map((tier: any) => (
              <tr key={tier.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <input
                      type="text"
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="input w-24 text-sm py-1"
                    />
                  ) : (
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{tier.name}</span>
                      {tier.is_default && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          Default
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <input
                      type="number"
                      value={editData.text_limit || ''}
                      onChange={(e) => setEditData({ ...editData, text_limit: parseInt(e.target.value) })}
                      className="input w-24 text-sm py-1"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {tier.text_limit?.toLocaleString()}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <input
                      type="text"
                      value={editData.weekly_cost_limit || ''}
                      onChange={(e) => setEditData({ ...editData, weekly_cost_limit: e.target.value })}
                      className="input w-20 text-sm py-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {tier.weekly_cost_limit ? `$${tier.weekly_cost_limit}` : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <input
                      type="text"
                      value={editData.monthly_price || ''}
                      onChange={(e) => setEditData({ ...editData, monthly_price: e.target.value })}
                      className="input w-20 text-sm py-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {tier.monthly_price ? `€${tier.monthly_price}/mo` : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <input
                      type="text"
                      value={editData.yearly_price || ''}
                      onChange={(e) => setEditData({ ...editData, yearly_price: e.target.value })}
                      className="input w-20 text-sm py-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {tier.yearly_price ? `€${tier.yearly_price}/yr` : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {tier.user_count ?? 0}
                </td>
                <td className="px-4 py-3">
                  {editingId === tier.id ? (
                    <div className="flex space-x-1">
                      <button
                        onClick={saveEdit}
                        disabled={updateMutation.isPending}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-gray-500 hover:text-gray-700 text-sm font-medium ml-2"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEdit(tier)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main Admin Page
const AdminPage: React.FC = () => {
  const location = useLocation();

  const tabs = [
    { path: '/admin', label: 'Dashboard' },
    { path: '/admin/users', label: 'Users' },
    { path: '/admin/tiers', label: 'Tiers' },
    { path: '/admin/prompts', label: 'Prompts' },
    { path: '/admin/models', label: 'Models' },
    { path: '/admin/settings', label: 'Settings' },
    { path: '/admin/costs', label: 'Costs' },
    { path: '/admin/errors', label: 'Errors' },
    { path: '/admin/system', label: 'System' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
        <p className="text-gray-500 dark:text-gray-400">Manage your application</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {tabs.map((tab) => (
            <Link
              key={tab.path}
              to={tab.path}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                location.pathname === tab.path
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <Routes>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="tiers" element={<AdminTiers />} />
        <Route path="prompts" element={<AdminPrompts />} />
        <Route path="models" element={<AdminModels />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="costs" element={<AdminCosts />} />
        <Route path="errors" element={<AdminErrors />} />
        <Route path="system" element={<AdminSystem />} />
      </Routes>
    </div>
  );
};

export default AdminPage;
